/**
 * Attachment endpoints: upload an image for a conversation, and mint a
 * short-lived URL to view one.
 */
import { FetchError } from "fetcher"
import type { AttachmentRef } from "../types"
import type { Jid } from "../xmpp/jid"
import { bearer, chatBaseUrl, chatFetch } from "./helpers"

const REJECTION_REASONS = ["type", "size", "infected", "undecodable"] as const
/**
 * Besides the reasons a refusal names: `not_allowed`, this user cannot upload
 * to the conversation (403/404); `other`, a reason this client does not know.
 */
export type RejectionReason =
  | (typeof REJECTION_REASONS)[number]
  | "not_allowed"
  | "other"

/**
 * What an upload came to. `unavailable` is worth retrying as is; `rejected` is
 * final for this file; `rate_limited` says how many seconds to wait.
 */
export type UploadOutcome =
  | { status: "clean"; attachment: AttachmentRef }
  | { status: "rejected"; reason: RejectionReason }
  | { status: "unavailable" }
  | { status: "rate_limited"; retryAfter: number }

/**
 * `POST /attachments?conversation=<jid>` with the raw image as the body. XHR
 * rather than `chatFetch`, because fetch reports no upload progress and
 * `FetchError` drops the `Retry-After` header. Statuses that are not an
 * outcome throw a `FetchError`, so a 401 re-auths like every other chat call.
 */
export const uploadAttachment = (
  sessionToken: string,
  conversation: Jid,
  file: Blob,
  onProgress: (fraction: number) => void
): Promise<UploadOutcome> =>
  new Promise((resolve, reject) => {
    const url = `${chatBaseUrl()}/attachments?${new URLSearchParams({ conversation })}`
    const xhr = new XMLHttpRequest()
    xhr.open("POST", url)
    for (const [name, value] of Object.entries(bearer(sessionToken))) {
      xhr.setRequestHeader(name, value)
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total)
    }
    xhr.onerror = () => resolve({ status: "unavailable" })
    xhr.onload = () => {
      const json = parseJson(xhr.responseText)
      const outcome = toOutcome(xhr, json)
      if (outcome) return resolve(outcome)

      reject(
        new FetchError({
          message: undefined,
          method: "POST",
          url,
          cause: undefined,
          status: xhr.status,
          text: xhr.responseText,
          json,
        })
      )
    }

    xhr.send(file)
  })

/** Every field an upload response body may carry; which ones depends on the status. */
type RawBody = Partial<{
  id: string
  width: number
  height: number
  reason: string
  retry_after: number
}>

/** The outcome a response stands for, or undefined when it is not one. */
function toOutcome(
  xhr: XMLHttpRequest,
  json: unknown
): UploadOutcome | undefined {
  const body = (json ?? {}) as RawBody

  switch (xhr.status) {
    case 201:
      return fromCreated(body)
    case 403:
    case 404:
      return fromNotAllowed()
    case 413:
      return fromTooLarge(body)
    case 422:
      return fromUnprocessable(body)
    case 429:
      return fromTooManyRequests(xhr, body)
    default:
      return fromOtherStatus(xhr.status)
  }
}

/** The clean Attachment, or unavailable when the body does not describe one. */
function fromCreated({ id, width, height }: RawBody): UploadOutcome {
  return id && width && height
    ? { status: "clean", attachment: { id, width, height } }
    : { status: "unavailable" }
}

/** This user cannot upload to the conversation; trying again will not help. */
function fromNotAllowed(): UploadOutcome {
  return { status: "rejected", reason: "not_allowed" }
}

/** Too large, whether or not a reason came with it. */
function fromTooLarge(body: RawBody): UploadOutcome {
  return { status: "rejected", reason: knownReason(body) ?? "size" }
}

/** Refused, and final even when we cannot say why. */
function fromUnprocessable(body: RawBody): UploadOutcome {
  return { status: "rejected", reason: knownReason(body) ?? "other" }
}

function fromTooManyRequests(
  xhr: XMLHttpRequest,
  body: RawBody
): UploadOutcome {
  // Neither hint usable: wait a minute rather than invite an immediate retry.
  const retryAfter =
    Number(xhr.getResponseHeader("retry-after")) ||
    Number(body.retry_after) ||
    60
  return { status: "rate_limited", retryAfter }
}

/**
 * Any 5xx is as transient as a 503 (a restarting proxy answers 502/504);
 * anything else is not an outcome.
 */
function fromOtherStatus(status: number): UploadOutcome | undefined {
  return status >= 500 ? { status: "unavailable" } : undefined
}

function knownReason({ reason }: RawBody): RejectionReason | undefined {
  return REJECTION_REASONS.find((r) => r === reason)
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

/**
 * `GET /attachments/:id/url` — a signed URL that works for 15 minutes; a 404
 * when the Attachment is gone or not ours to see.
 */
export const getAttachmentUrl = (sessionToken: string, id: string) =>
  chatFetch<string, "json">({
    baseUrl: chatBaseUrl(),
    path: `/attachments/${encodeURIComponent(id)}/url`,
    headers: bearer(sessionToken),
    responseType: "json",
    transform: (data) => (data as { url: string }).url,
  })
